import {
  FieldName,
  FieldSchema,
  SchemaAspects,
  SchemaAspectsMapping,
} from "../Schema";

/**
 * The aspect half of a structure entry: any subset of the schema aspects, used to
 * override the base schema for one field in one structure.
 */
export type StructureAspects = Partial<SchemaAspectsMapping>;

/**
 * The minimal structure entry every consumer shares: a field name plus the aspect
 * overrides applied on top of that field's base schema. UI structures extend this
 * with presentation properties (type, width, params, ...) — see the React package's
 * `BasicFieldStructure`; headless structures such as the CSV one use it as-is.
 */
export type FieldStructureEntry = { name: FieldName } & StructureAspects;

/**
 * Shallow-merges every schema aspect across the given sources, later sources winning.
 * Each aspect is merged independently, so an override touching only `csv` leaves the
 * field's `data`, `validation` and `form` aspects intact.
 *
 * Every aspect key is present on the result — including as an empty object when no
 * source defines it — because managers read aspects with `field.data?.type`, for which
 * an empty object and an absent one are equivalent.
 *
 * @param sources - Aspect carriers to merge, in increasing order of precedence.
 * @returns The merged aspects.
 */
export const mergeAspects = (
  ...sources: (StructureAspects | undefined)[]
): SchemaAspectsMapping => {
  const merged = {} as SchemaAspectsMapping;
  Object.values(SchemaAspects).forEach(<A extends SchemaAspects>(aspect: A) => {
    merged[aspect] = sources.reduce(
      (accumulated, source) => ({
        ...accumulated,
        ...(source?.[aspect] ?? {}),
      }),
      {}
    ) as SchemaAspectsMapping[A];
  });
  return merged;
};

/**
 * Builds the effective schema of a single field by merging its base schema with the
 * structure entry's overrides.
 *
 * @param name - Name of the field.
 * @param sources - Aspect carriers to merge, in increasing order of precedence.
 * @returns The field schema with all overrides applied.
 */
export const prepareFieldSchema = (
  name: FieldName,
  ...sources: (StructureAspects | undefined)[]
): FieldSchema => ({
  ...mergeAspects(...sources),
  name,
});
